/**
 * AIBlogCostWidget Component
 *
 * Displays monthly AI blog generation cost analytics including:
 * - Total cost, posts generated, average cost per post
 * - Month-over-month comparison with percentage changes
 * - Cost breakdown by generation phase
 *
 * Story: BLOG-AI-004, AC-4, AC-5, AC-7, AC-8, AC-9
 */
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Sparkles, DollarSign, FileText, TrendingUp, ArrowUpIcon, ArrowDownIcon, Loader2, AlertCircle } from 'lucide-react'
import { apiService } from '@/services/api'
import type { AIAnalyticsResponse } from '@/types/blog.types'
import { Button } from '@/components/ui/button'
import { CostBreakdownChart } from './CostBreakdownChart'

export function AIBlogCostWidget() {
  // Fetch analytics data
  const {
    data: analytics,
    isLoading,
    error,
    refetch,
  } = useQuery<AIAnalyticsResponse>({
    queryKey: ['ai-blog-analytics'],
    queryFn: () => apiService.blog.aiWriter.getAnalytics(),
    // Adaptive polling: 30s normally, 5s if there are active generations
    refetchInterval: 30000,
    retry: 3,
  })

  // Get current month name for display
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  // Error state
  if (error) {
    return (
      <Card className="bg-card text-card-foreground border-border">
        <CardContent className="py-8">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-3" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Failed to load analytics
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Unable to fetch AI blog cost data. Please try again.
            </p>
            <Button
              onClick={() => refetch()}
              variant="outline"
              className="border-border hover:bg-accent"
            >
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <Card className="bg-card text-card-foreground border-border">
        <CardHeader>
          <CardTitle className="text-foreground">AI Blog Generation Costs</CardTitle>
          <CardDescription className="text-muted-foreground">{currentMonth} overview</CardDescription>
        </CardHeader>
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Empty state - no AI posts generated
  if (!analytics || analytics.posts_generated === 0) {
    return (
      <Card className="bg-card text-card-foreground border-border">
        <CardHeader>
          <CardTitle className="text-foreground">AI Blog Generation Costs</CardTitle>
          <CardDescription className="text-muted-foreground">{currentMonth} overview</CardDescription>
        </CardHeader>
        <CardContent className="py-12">
          <div className="text-center">
            <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No AI blog posts yet
            </h3>
            <p className="text-sm text-muted-foreground">
              Use the AI Writer to create your first post and start tracking costs
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="bg-card text-card-foreground border-border">
      <CardHeader>
        <CardTitle className="text-foreground">AI Blog Generation Costs</CardTitle>
        <CardDescription className="text-muted-foreground">{currentMonth} overview</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Main Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Total Cost */}
          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <DollarSign className="h-4 w-4 text-primary" />
              </div>
              {analytics.comparison && (
                <div
                  className={`flex items-center text-sm font-medium ${
                    analytics.comparison.cost_change_percent > 0
                      ? 'text-red-600 dark:text-red-400'
                      : analytics.comparison.cost_change_percent < 0
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-muted-foreground'
                  }`}
                >
                  {analytics.comparison.cost_change_percent > 0 ? (
                    <ArrowUpIcon className="h-4 w-4 mr-1" />
                  ) : analytics.comparison.cost_change_percent < 0 ? (
                    <ArrowDownIcon className="h-4 w-4 mr-1" />
                  ) : null}
                  {Math.abs(analytics.comparison.cost_change_percent)}%
                </div>
              )}
            </div>
            <div className="text-2xl font-bold text-foreground">
              ${analytics.total_cost.toFixed(2)}
            </div>
            <div className="text-sm text-muted-foreground">Total Cost</div>
          </div>

          {/* Posts Generated */}
          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <FileText className="h-4 w-4 text-primary" />
              </div>
              {analytics.comparison && analytics.comparison.posts_change_percent !== 0 && (
                <div
                  className={`flex items-center text-sm font-medium ${
                    analytics.comparison.posts_change_percent > 0
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {analytics.comparison.posts_change_percent > 0 ? (
                    <ArrowUpIcon className="h-4 w-4 mr-1" />
                  ) : (
                    <ArrowDownIcon className="h-4 w-4 mr-1" />
                  )}
                  {Math.abs(analytics.comparison.posts_change_percent)}%
                </div>
              )}
            </div>
            <div className="text-2xl font-bold text-foreground">
              {analytics.posts_generated}
            </div>
            <div className="text-sm text-muted-foreground">Posts Generated</div>
          </div>

          {/* Average Cost per Post */}
          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
            </div>
            <div className="text-2xl font-bold text-foreground">
              ${analytics.average_cost.toFixed(2)}
            </div>
            <div className="text-sm text-muted-foreground">Avg Cost/Post</div>
          </div>
        </div>

        {/* Cost Breakdown Chart */}
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-foreground mb-3">
            Cost by Generation Phase
          </h4>
          <CostBreakdownChart data={analytics.cost_by_phase} />
        </div>

        {/* Failed Posts Indicator (if any) */}
        {analytics.failed_posts > 0 && (
          <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-sm text-foreground">
              <span className="font-semibold">{analytics.failed_posts}</span> post{analytics.failed_posts > 1 ? 's' : ''} failed to generate this month
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
